import CoreLoader from '@components/CoreLoader'
import cn from 'classnames'
import { PaymentMethods, PaymentOptionStatuses } from 'mmfintech-backend-api'
import type { PaymentOptionResponse } from 'mmfintech-backend-api/types'
import { isValidArray, useMatchMedia } from 'mmfintech-commons'
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react'
import PaymentMethodItem from './PaymentMethodItem'

import styles from './PaymentMethodSelect.module.scss'

const COLLAPSE_BREAKPOINT_PX = 1280
const COLLAPSE_TRANSITION_MS = 300
const COLLAPSED_ITEM_HEIGHT_REM = 8.8

interface MorePaymentOptions {
  description?: string
  title?: string
  dataTest?: string
  path?: string
  isDisabled?: boolean
}

export type PaymentMethodOption = PaymentOptionResponse & MorePaymentOptions

export function isMethodDisabled(
  method: PaymentMethodOption,
  { disabled = false, ignoreStatus = false }: { disabled?: boolean; ignoreStatus?: boolean } = {}
): boolean {
  if (disabled || method.isDisabled) return true
  if (ignoreStatus) return false
  if (method.status === PaymentOptionStatuses.AVAILABLE) return false
  return !(
    method.status === PaymentOptionStatuses.INCOMPLETE_ONBOARDING && method.paymentMethod === PaymentMethods.BINANCE_PAY
  )
}

type PaymentMethodSelectProps = {
  paymentOptions: PaymentMethodOption[]
  setSelectedPaymentOption?: (value: string, path?: string) => void
  selectedPaymentOption?: PaymentOptionResponse
  collapse?: boolean
  loading?: boolean
  disabled?: boolean
  setCollapse?: Dispatch<SetStateAction<boolean>>
  ignoreStatus?: boolean
  className?: string
}

const PaymentMethodSelect = (props: PaymentMethodSelectProps) => {
  const {
    paymentOptions,
    setSelectedPaymentOption,
    selectedPaymentOption,
    collapse,
    disabled = false,
    setCollapse,
    ignoreStatus = false,
    className,
    loading
  } = props

  const enableCollapse = useMatchMedia({ breakpoint: COLLAPSE_BREAKPOINT_PX, prefix: 'max' })
  const containerRef = useRef<HTMLDivElement>(null)
  const [filteredOptions, setFilteredOptions] = useState<PaymentMethodOption[]>(paymentOptions)

  useEffect(() => {
    const activeMethod = selectedPaymentOption?.paymentMethod
    if (enableCollapse && isValidArray(paymentOptions) && activeMethod) {
      const timer = setTimeout(() => {
        setFilteredOptions(paymentOptions.filter(option => option.paymentMethod !== activeMethod))
      }, COLLAPSE_TRANSITION_MS)
      return () => clearTimeout(timer)
    }
    setFilteredOptions(paymentOptions)
  }, [enableCollapse, selectedPaymentOption, paymentOptions])

  useEffect(() => {
    if (enableCollapse && !collapse) {
      containerRef.current?.scroll(0, 0)
    }
  }, [collapse, enableCollapse])

  if (loading) return <CoreLoader />

  const collapsedHeight = COLLAPSED_ITEM_HEIGHT_REM * (filteredOptions.length + (selectedPaymentOption ? 1 : 0))

  return (
    <div
      ref={containerRef}
      className={cn(styles['payment-method-select'], 'payment-methods-wrapper')}
      style={{
        height: enableCollapse && collapse ? `${collapsedHeight}rem` : undefined,
        overflow: enableCollapse && !collapse ? 'hidden' : 'auto'
      }}
    >
      {enableCollapse && selectedPaymentOption && (
        <PaymentMethodItem method={selectedPaymentOption} variant='collapsed' onClick={() => setCollapse?.(true)} />
      )}
      {isValidArray(filteredOptions) &&
        filteredOptions.map(method => (
          <PaymentMethodItem
            key={method.paymentMethod}
            method={method}
            variant='list'
            fullWidth={filteredOptions.length === 1}
            selected={!enableCollapse && selectedPaymentOption?.paymentMethod === method.paymentMethod}
            disabled={isMethodDisabled(method, { disabled, ignoreStatus })}
            onClick={() => setSelectedPaymentOption?.(method.paymentMethod, method.path)}
            className={className}
          />
        ))}
    </div>
  )
}

export default PaymentMethodSelect
